import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_SINGLE_ROLE_PERMISSION } from '../../../utils/apollo/permission';
import {
  IRolePermissionQuery,
  IRolePermissionQueryVariables,
  IRoleWithPermissionsFieldsFragment,
  RoleType,
} from '../../../utils/apollo/graphqlSchema';
import {
  ClickablePageTitle,
  PageHeaderDivider,
} from '../../../components/PageHeader';
import { useParams } from 'react-router-dom';
import { assertCondition, assertNotNil } from '../../../utils/types';
import { appRoutes } from '../../../path';
import * as PermissionControlsUI from '../../PermissionControls/PermissionControlsUI';
import {
  usePermissionEntityDescribeTable,
  usePermissionOverviewList,
} from '../../PermissionControls/hooks';
import PermissionEntityDescribeTable from '../../PermissionControls/PermissionEntityDescribeTable';
import PermissionSchemasSelector from '../../PermissionControls/PermissionSchemasSelector';
import styled from 'styled-components';
import PermissionEntityToggleButton from '../../PermissionControls/PermissionEntityToggleButton';
import ExpandableTable from '../../../components/ExpandableTable';
import { useBootstrapSelector } from '../../../redux/selectorHooks';
import { IBasicSchema } from '../../PermissionControls/types';
import { ContentLoader } from '../../../components/Loaders';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { pageViewed } from '../../../utils/analytics/events/page/events';
import ThinDivider from '../../../components/ThinDivider';
import { theme } from '../../../styles/theme';

const TableHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 320px auto;
  column-gap: 12px;
`;
const TableHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.secondary900};
`;

/**
 * RoleOverview is the page for displaying the overview of a role. it contains name/description and the
 * permission table of the role.
 */
const RoleOverview = () => {
  const permissionConfig = useBootstrapSelector('permissionConfig');
  assertNotNil(permissionConfig);
  const [role, setRole] = useState<IRoleWithPermissionsFieldsFragment | null>(
    null,
  );
  const [schemaFilter, setSchemaFilter] = useState<IBasicSchema>(
    permissionConfig.allSchema,
  );
  const urlParams = useParams();
  const roleId = urlParams.roleId;
  assertNotNil(roleId);

  const { track } = useAnalytics();
  useEffect(() => {
    track(
      pageViewed({
        category: 'settings',
        name: 'view role',
        id: roleId,
      }),
    );
  }, [roleId, track]);

  const { contentList } = usePermissionEntityDescribeTable({ entity: role });
  const { displayedTableList } = usePermissionOverviewList({
    permissionAssignments: role?.permissionAssignments ?? [],
    schemaFilter,
  });

  const onFetchRole = ({ role }: IRolePermissionQuery) => {
    setRole(role);
  };
  const { loading, refetch } = useQuery<
    IRolePermissionQuery,
    IRolePermissionQueryVariables
  >(GET_SINGLE_ROLE_PERMISSION, {
    variables: {
      id: roleId,
    },
    onCompleted: onFetchRole,
  });

  const onSchemaFilterChange = (schemas: IBasicSchema[]) => {
    assertCondition(schemas.length === 1);
    setSchemaFilter(schemas[0]);
  };
  if (!role || loading) {
    return <ContentLoader />;
  }
  const titleList = [
    { name: 'Roles', path: appRoutes.permission.roles },
    { name: role.name, path: '' },
  ];
  return (
    <>
      <ClickablePageTitle titleList={titleList} />
      <PageHeaderDivider />
      <PermissionControlsUI.OverviewContainer>
        <PermissionEntityDescribeTable contentList={contentList} />
        <ThinDivider layout="horizontal" size="full" />
        <TableHeaderRow>
          <TableHeader>Permissions</TableHeader>
          <PermissionSchemasSelector
            setSchemas={onSchemaFilterChange}
            selectedSchemas={[schemaFilter]}
            schemaOptions={permissionConfig.schemas}
            isMulti={false}
          />
        </TableHeaderRow>
        <ExpandableTable tableList={displayedTableList} />
        {/* hide disable/enable button for Role:Admin */}
        {role.type !== RoleType.Admin && (
          <>
            <ThinDivider layout="horizontal" size="full" />
            <PermissionEntityToggleButton
              entity={role}
              refetch={refetch}
              isActive={role.active}
            />
          </>
        )}
      </PermissionControlsUI.OverviewContainer>
    </>
  );
};

export default RoleOverview;
